import { isVideoStation } from '@app/core/stations/helpers';
import { useDiarization } from '@app/modules/audit/hooks/useDiarization';
import {
  isDiarizationSegmentsEnabled,
  isTranscriptionEnabled,
} from '@app/modules/fragments/helpers/is-transcription-enabled';
import { parseRangeFromTs } from '@mi-front/core';
import { noop } from 'lodash';
import React from 'react';
import { useActiveStation } from '../../../../../core/stations/hooks/useActiveStation';
import { TranscriptionType } from '../components/audit/transcriptions/transcriptions.types';
import { useStreamSegmentsRange } from '../hooks/segments/useStreamSegmentsQuery';

type Props = {
  children?: React.ReactNode;
};

export interface DiarizationContextProps {
  speakers: { speaker: string; color: string }[];
  diarization: any[];
  diarizationRefetch: () => void;
  rawData: TranscriptionType[];
}

export const diarizationInitialValues: DiarizationContextProps = {
  speakers: [],
  diarization: [],
  diarizationRefetch: noop,
  rawData: [],
};

export const DiarizationContext =
  React.createContext<DiarizationContextProps>(diarizationInitialValues);

export const DiarizationProvider: React.FC<Props> = (props) => {
  const { children } = props;
  const station = useActiveStation();
  const diarizationEnabled =
    isDiarizationSegmentsEnabled(station) ||
    (!isVideoStation(station) && isTranscriptionEnabled(station));
  const range = useStreamSegmentsRange();

  const {
    data: diarization,
    refetch: diarizationRefetch,
    speakers,
    rawData,
  } = useDiarization(station, parseRangeFromTs(range), diarizationEnabled);

  const contextValue: DiarizationContextProps = React.useMemo(
    () => ({
      ...diarizationInitialValues,
      speakers: speakers.current,
      diarization: diarization,
      diarizationRefetch: diarizationRefetch,
      rawData: rawData,
    }),
    [diarization, diarizationRefetch, speakers, rawData],
  );

  return <DiarizationContext.Provider value={contextValue}>{children}</DiarizationContext.Provider>;
};
